import { router } from "@inertiajs/react";
import { CheckCircle, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Candidate, ExcludedFirm, PackageData, SkmBlast, csrfToken, fmt } from "./_types";
import ExcludedPanel from "./ExcludedPanel";

interface Props {
  pkg: PackageData;
  candidate: Candidate;
  skm_blast: SkmBlast | null;
  excluded: ExcludedFirm[];
}

const CONTACT_STATUS_COLORS: Record<string, string> = {
  queued:         "bg-gray-100 text-gray-500",
  sent:           "bg-blue-100 text-blue-700",
  responded:      "bg-yellow-100 text-yellow-700",
  interested:     "bg-green-100 text-green-700",
  not_interested: "bg-red-50 text-red-500",
};

export default function SkmSection({ pkg, candidate, skm_blast, excluded }: Props) {
  const reviewed = pkg.skm_status === "approved" || pkg.skm_status === "skipped";

  function markReviewed() {
    router.patch(`/candidates/${candidate.id}/package`, {
      package: { skm_status: "approved" },
    });
  }

  function removeContact(skmContactId: number) {
    router.delete(`/candidates/${candidate.id}/package/skm_contact`, {
      data: { skm_contact_id: skmContactId, authenticity_token: csrfToken() },
    });
  }

  return (
    <Card className={reviewed ? "border-green-200" : ""}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {reviewed && <CheckCircle className="w-4 h-4 text-green-500" />}
            SKM Blast
            {skm_blast && (
              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                {skm_blast.contact_count} contact{skm_blast.contact_count !== 1 ? "s" : ""}
              </span>
            )}
          </CardTitle>
          {!reviewed && (
            <Button variant="outline" size="sm" onClick={markReviewed}>Mark Reviewed</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!skm_blast ? (
          <p className="text-sm text-gray-400 text-center py-6">
            No SKM blast generated yet.
          </p>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-3 text-xs text-gray-500">
              <span className={`px-2 py-0.5 rounded-full capitalize font-medium ${
                skm_blast.status === "sent"     ? "bg-green-100 text-green-700" :
                skm_blast.status === "approved" ? "bg-blue-100 text-blue-700"  :
                "bg-gray-100 text-gray-500"
              }`}>{skm_blast.status}</span>
              {skm_blast.sent_at && <span>Sent {fmt(skm_blast.sent_at)}</span>}
            </div>

            {skm_blast.contacts.length > 0 && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400 border-b border-gray-100">
                    <th className="text-left py-1.5 font-medium">Contact</th>
                    <th className="text-left py-1.5 font-medium">Firm</th>
                    <th className="text-left py-1.5 font-medium">Status</th>
                    {!reviewed && <th className="w-6" />}
                  </tr>
                </thead>
                <tbody>
                  {skm_blast.contacts.map((bc) => (
                    <tr key={bc.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-1.5 text-gray-800">{bc.contact_name}</td>
                      <td className="py-1.5 text-gray-500">{bc.firm_name}</td>
                      <td className="py-1.5">
                        <span className={`px-1.5 py-0.5 rounded capitalize ${CONTACT_STATUS_COLORS[bc.status] ?? "bg-gray-100 text-gray-500"}`}>
                          {bc.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      {!reviewed && (
                        <td className="py-1.5">
                          <button
                            onClick={() => removeContact(bc.skm_contact_id)}
                            className="text-gray-300 hover:text-red-500 transition-colors"
                            title="Remove contact"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
        <ExcludedPanel excluded={excluded} />
      </CardContent>
    </Card>
  );
}
